import Button, { AnchorPropsBase, type ButtonProps } from '@pelckmans/elementis/components/button';
import DigibookIcon from '@pelckmans/elementis/icons/digibook';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../../constants/analyticsEvents';
import AccountContext from '../../../../../../contexts/AccountContext';
import useAnalytics from '../../../../../../hooks/useAnalytics';
import ModuleContext from '../../../../_contexts/ModuleContext';
import { type App } from '../../../../api/appsApi';
import DigibookButton from '../../../../components/DigibookButton';
import {
  buildCodexUrl,
  getDisabledTooltip,
  isDigibookApp,
  isOldDigibookApp,
  type DigibookApp,
  type OldDigibookApp,
} from '../../../../utils/digibookAppUtils';
import { type MobileDigibookDropdownItem } from '../MobileAppsDropdownButton';

const DIGIBOOK_MANUAL_INCLUDED_KEY = 'PAGES.TOC_LEVEL.APPS.DIGIBOOK.MANUAL_INCLUDED';

function getDigibookButtonText(
  t: ReturnType<typeof useTranslation>['t'],
  digibookApp: { linkedApp?: { manualIncluded?: boolean } },
  pageNumber: number,
) {
  if (pageNumber > 0) {
    return t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.OPEN_DIGIBOOK_WITH_PAGE', { page: pageNumber });
  }

  if (digibookApp.linkedApp?.manualIncluded) {
    return t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.OPEN_DIGIBOOK_WITH_MANUAL');
  }

  return t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.OPEN_DIGIBOOK');
}

function getDigibookSubText(
  t: ReturnType<typeof useTranslation>['t'],
  digibookApp: { linkedApp?: { manualIncluded?: boolean } },
) {
  return digibookApp.linkedApp?.manualIncluded ? t(DIGIBOOK_MANUAL_INCLUDED_KEY) : undefined;
}

function getTranslatedDisabledTooltip(
  t: ReturnType<typeof useTranslation>['t'],
  digibookApp: Parameters<typeof getDisabledTooltip>[0],
) {
  const disabledTooltip = getDisabledTooltip(digibookApp);

  return {
    disabled: Boolean(disabledTooltip),
    tooltip: disabledTooltip ? t(disabledTooltip) : undefined,
  };
}

function DigibookPageButton({
  digibookApp,
  pageNumber,
  size,
  emphasized,
  href,
  onClick,
}: {
  digibookApp: DigibookApp;
  pageNumber: number;
} & Pick<ButtonProps, 'size' | 'emphasized'> &
  Pick<AnchorPropsBase, 'href' | 'onClick'>) {
  const { t } = useTranslation();

  const disabledTooltip = getDisabledTooltip(digibookApp);

  return (
    <Button
      as="link"
      prominence="secondary"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      size={size}
      startIcon={<DigibookIcon data-testid="digibook-icon" />}
      disabled={!!disabledTooltip}
      tooltip={disabledTooltip ? t(disabledTooltip) : undefined}
      onClick={onClick}
      emphasized={emphasized}
    >
      {getDigibookButtonText(t, digibookApp, pageNumber)}
    </Button>
  );
}

export default function useDigibookApps({
  apps,
  selectedModuleId,
  showDigibooks,
  pageNumber,
  size,
}: {
  apps?: App[];
  selectedModuleId?: string;
  showDigibooks?: boolean;
  pageNumber?: number;
  size?: ButtonProps['size'];
} = {}) {
  const { t } = useTranslation();
  const {
    account: { provider },
  } = useContext(AccountContext);
  const { module, selectedModule } = useContext(ModuleContext);
  const { capture } = useAnalytics();

  const subLocationId = selectedModule?.isExerciseModule
    ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_MATERIAL
    : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL;

  const getDigibookHref = ({ digibookApp, pageNumber }: { digibookApp: App; pageNumber?: number }) => {
    if (digibookApp.platformUrl) return digibookApp.platformUrl;
    if (!digibookApp.linkedApp?.id) return '';

    return module
      ? buildCodexUrl({
          digibookId: digibookApp.linkedApp.id,
          superModuleId: module.id,
          accountProvider: provider,
          page: pageNumber,
        })
      : '';
  };

  const captureNewDigibookOpen = (app: App) => {
    capture({
      subLocationId,
      eventType: ANALYTICS_EVENT_TYPES.DIGIBOOK_ACCESSED,
      objectId: app.id.toString(),
    });

    if (app.linkedApp?.id) {
      capture({
        subLocationId,
        eventType: ANALYTICS_EVENT_TYPES.APP_ACCESSED,
        objectId: app.linkedApp.id.toString(),
      });
    }
  };

  const captureOldDigibookOpen = (app: App) => {
    capture({
      subLocationId,
      eventType: ANALYTICS_EVENT_TYPES.DIGIBOOK_ACCESSED,
      objectId: app.platformUrl || app.linkedApp?.id || app.id,
    });

    capture({
      subLocationId,
      eventType: ANALYTICS_EVENT_TYPES.APP_ACCESSED,
      objectId: app.id,
    });
  };

  const digibookApp = useMemo(
    () =>
      apps?.find(
        (app): app is DigibookApp =>
          Boolean(selectedModuleId) && isDigibookApp(app) && app.moduleId === selectedModuleId,
      ),
    [apps, selectedModuleId],
  );

  const oldDigibookApps = useMemo(
    () =>
      apps?.filter(
        (app): app is OldDigibookApp =>
          Boolean(selectedModuleId) && isOldDigibookApp(app) && app.moduleId === selectedModuleId,
      ) ?? [],
    [apps, selectedModuleId],
  );

  const shouldShowDigibooks = Boolean(showDigibooks);
  const resolvedPageNumber = pageNumber ?? 0;
  const hasDigibookApps = Boolean(digibookApp) || oldDigibookApps.length > 0;

  const mobileDigibookItems: MobileDigibookDropdownItem[] = shouldShowDigibooks
    ? [
        ...(digibookApp
          ? [
              {
                id: digibookApp.id,
                text: getDigibookButtonText(t, digibookApp, resolvedPageNumber),
                subText: getDigibookSubText(t, digibookApp),
                href: getDigibookHref({ digibookApp, pageNumber: resolvedPageNumber }),
                ...getTranslatedDisabledTooltip(t, digibookApp),
                onClick: () => captureNewDigibookOpen(digibookApp),
              },
            ]
          : []),
        ...oldDigibookApps.map(oldDigibookApp => {
          return {
            id: oldDigibookApp.id,
            text: `${oldDigibookApp.module.catalogName}: ${oldDigibookApp.name}`,
            subText: getDigibookSubText(t, oldDigibookApp),
            href: getDigibookHref({ digibookApp: oldDigibookApp }),
            ...getTranslatedDisabledTooltip(t, oldDigibookApp),
            onClick: () => captureOldDigibookOpen(oldDigibookApp),
          };
        }),
      ]
    : [];

  const digibookButtons = shouldShowDigibooks
    ? [
        ...(digibookApp
          ? [
              <DigibookPageButton
                key="digibook"
                digibookApp={digibookApp}
                pageNumber={resolvedPageNumber}
                size={size}
                href={getDigibookHref({ digibookApp, pageNumber: resolvedPageNumber })}
                onClick={() => captureNewDigibookOpen(digibookApp)}
              />,
            ]
          : []),
        <DigibookButton key="old-digibook" digibookApps={oldDigibookApps} size={size} />,
      ]
    : [];

  return { digibookButtons, mobileDigibookItems, hasDigibookApps };
}
